import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, AlertCircle, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const backgroundImage = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80";

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check for fallback registration and auth data
  useEffect(() => {
    const isRegisteredWithFallback = localStorage.getItem('isRegisteredWithFallback') === 'true';
    if (isRegisteredWithFallback) {
      // Pre-fill username if available
      const email = localStorage.getItem('userEmail');
      if (email) {
        setUsername(email);
      }
    }
    
    // Preload background image
    new Image().src = backgroundImage;
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Try Supabase Auth login
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: username,
        password: password
      });

      if (authError) {
        console.error('Auth error:', authError);
        throw authError;
      }

      if (authData.user) {
        console.log('Auth successful:', authData.user);

        // Check user role from app_metadata
        const userRole = authData.user.app_metadata?.role;
        
        if (userRole === 'admin' || userRole === 'editor') {
          // Admin/Editor login successful
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userRole', userRole);
          localStorage.setItem('userEmail', authData.user.email);
          localStorage.setItem('companyName', authData.user.user_metadata?.company_name || '911 Cargo');
          
          window.location.href = '/client/dashboard';
          return;
        }
      }
      
      // Fallback: Check if user exists in 911cargoprofile
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('911cargoprofile')
          .select('*')
          .eq('email', username)
          .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully
          
        if (profileError) {
          console.error('Error fetching profile:', profileError);
          throw new Error('Ошибка при проверке профиля');
        }
          
        if (profileData) {
          // User exists in the profile table, allow login with fallback
          localStorage.setItem('userFirstName', profileData.first_name);
          localStorage.setItem('userLastName', profileData.last_name);
          localStorage.setItem('userPhone', profileData.phone);
          localStorage.setItem('userEmail', profileData.email);
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('isAdmin', 'false');
          localStorage.setItem('userId', profileData.id);
          localStorage.setItem('isLoggedInWithFallback', 'true');
          
          navigate('/client/dashboard');
          return;
        } else {
          setError('Пользователь не найден');
        }
      } catch (profileError: any) {
        console.error('Error checking profile:', profileError);
        setError('Ошибка при проверке профиля');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.error_description || error.message || 'Ошибка при входе');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white md:flex">
      {/* Mobile Close Button */}
      <Link 
        to="/"
        className="md:hidden fixed top-16 right-4 w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 bg-gray-100 rounded-full z-50"
      >
        <X className="w-5 h-5" />
      </Link>

      {/* Left Side - Random Image (Desktop Only) */}
      <div className="hidden md:block md:w-2/3 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full md:w-1/3 min-h-screen md:min-h-0 flex flex-col items-center justify-center p-6 bg-white">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">Вход в личный кабинет</h1>
          <p className="text-gray-600">Введите данные для входа в систему</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-8 w-full max-w-md shadow-lg">
          <form onSubmit={handleLogin}>
            <div className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="email"
                    autoComplete="email"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                    placeholder="Введите email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Пароль
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-[#1B3BA0] focus:border-[#1B3BA0]"
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-[#1B3BA0] focus:ring-[#1B3BA0] border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600">
                    Запомнить меня
                  </label>
                </div>

                <div className="text-sm"></div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-[#1B3BA0] hover:bg-[#162D7A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3BA0] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Вход...' : 'Войти'}
                </button>
              </div>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Или</span>
                  </div>
                </div>

                <div className="mt-6">
                  <Link
                    to="/register"
                    className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B3BA0] transition-colors"
                  >
                    Зарегистрироваться
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;